/**
 * A tela espelho do Orçamento, dentro da ficha do Negócio (5.3, FR-43, FR-45, FR-46, FR-62).
 *
 * **Ele substitui o `CardReservado` que a 3.10 deixou no lugar**, e é essa a diferença que a
 * história entrega ao SPA: onde havia "O Orçamento espelho chega no Épico 5" há agora o Orçamento
 * de verdade — número, valor, validade e as linhas. O layout de duas colunas não se mexeu: a 3.10
 * reservou a seção justamente para que esta história não precisasse refazer a tela.
 *
 * **O selo "Espelho · leitura" continua, e agora ele é verdade.** Não há aqui botão de criar, de
 * editar nem de excluir, e a ausência não é escolha de tela: **não existe rota**. O preço é travado
 * pelo ERP (FR-45), e o que muda o Orçamento é o Carga Máquina. Quem quiser mudar o que está aqui
 * altera lá e reexporta — o espelho só muda com envio novo do conector (FR-46).
 *
 * **Nada nesta tela fala com o ERP.** A rota lê a tabela do CRM: com o Carga Máquina fora do ar o
 * cartão renderiza normalmente, e não há aqui estado de "ERP indisponível" para desenhar. É a
 * diferença entre um espelho e um cache — espelho não busca.
 *
 * **Três estados de leitura, e os três estão escritos.** Falha do serviço vira a mensagem dele,
 * verbatim, em `role="alert"`; enquanto a resposta não chegou, um `role="status"` diz que está
 * carregando; lista vazia é o estado vazio. Sem o terceiro, "não há Orçamento" e "ainda não sei"
 * ficariam iguais — e a tela afirmaria ausência onde só há desconhecimento.
 *
 * **Para a SDR o dinheiro não existe no cartão** (11.4, State Patterns): a chave `valor` não vem
 * (FR-62), e então nem o valor do cabeçalho nem as colunas de preço e total são desenhados — nem
 * traço, nem frase. Para quem vê dinheiro, `null` é "o ERP não mandou valor" (`SEM_ORCAMENTO`) e o
 * número é o valor; o texto sai de `textoDoValor`, a mesma função da ficha.
 *
 * **Duas árvores, escolhidas por `useEhCelular()` — e não um `@media`.** No computador as linhas do
 * Orçamento são uma tabela com cabeçalho; no celular, uma lista de blocos com rótulo em cima do
 * valor. É o mesmo desenho de `RecebimentosDoConector`, e pela mesma razão: uma tabela de quatro
 * colunas a 375 px vira rolagem horizontal, e rolagem horizontal dentro de uma coluna que já rola é
 * onde a informação se perde. A paridade da NFR-1 é de **dado**: as duas árvores mostram os mesmos
 * campos.
 *
 * **A quantidade é formatada em pt-BR, e não impressa como veio.** O serviço manda `"2.5"` — um
 * decimal em texto, no formato do contrato — e a tela mostraria `2.5` ao lado de `R$ 1.500,50`, com
 * dois separadores decimais diferentes na mesma linha. `QUANTIDADE` é o `Intl.NumberFormat` que
 * fecha isso, no molde de `apresentacao.ts`; o teto de casas é o mesmo do domínio, para que a tela
 * não arredonde o que o serviço aceitou.
 *
 * **A validade é uma data pura, e ela não passa por `Date`.** O serviço a declara `format: date`
 * (`2026-09-30`), e `new Date('2026-09-30')` é meia-noite **UTC** — que em São Paulo já é o dia 29.
 * `dataPuraPorExtenso` reordena os três pedaços como texto, que é o que uma data sem fuso pede.
 */

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { dataPuraPorExtenso, textoDoValor } from '@/modulos/negocios/apresentacao'
import { useEhCelular } from '@/shell/Shell'
import { type ItemDoOrcamento, type OrcamentoDoNegocio, useOrcamentosDoNegocio } from './api'
import './orcamento-do-negocio.css'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'orcamento-do-negocio-titulo'

const TITULO = 'Orçamento'

/**
 * O selo da 3.10, mantido literal.
 *
 * Ele já estava no `CardReservado`, e continua aqui porque agora ele **descreve** o cartão em vez de
 * prometer: o que está abaixo dele é somente leitura, e nenhuma ação existe.
 */
const SELO = 'Espelho · leitura'

const CARREGANDO = 'Carregando os orçamentos…'

/**
 * O estado vazio, e ele diz as duas coisas que quem abriu precisa saber: não há Orçamento **neste**
 * Negócio, e de onde ele viria.
 *
 * Ele já não anuncia épico nenhum — anunciar um dentro de um cartão que **já** mostra Orçamento
 * quando existe diria que a funcionalidade não chegou, sobre a tela que a entrega. É a mesma troca
 * que `SEM_TAREFAS` fez na 4.2.
 */
const VAZIO =
  'Nenhum orçamento veio do ERP ainda. Ele aparece aqui quando o Carga Máquina exportar — o CRM ' +
  'não edita orçamento.'
/** O texto do protótipo v2, com o código que se digita no "Exportar" do Carga Máquina. */
function vazioComCodigo(codigo: number | undefined): string {
  return codigo === undefined
    ? VAZIO
    : `Nenhum orçamento veio do ERP ainda. Exporte pelo Carga Máquina com o ID ${String(codigo)}.`
}

/** O traço do campo que o ERP não mandou. O contrato do conector não exige nenhum dos quatro. */
const VAZIO_DO_CAMPO = '—'

/**
 * A quantidade de uma linha, em pt-BR (NFR-8).
 *
 * Ela mora aqui, e não em `modulos/negocios/apresentacao.ts`: quantidade de item de Orçamento é
 * vocabulário **deste** módulo, e o que as telas de Negócio compartilham é dinheiro e data. O molde
 * é o de lá — um `Intl.NumberFormat` de módulo, criado uma vez, com o literal preso por teste.
 *
 * **`maximumFractionDigits` é o teto do domínio** (`CASAS_DECIMAIS_DA_QUANTIDADE_DO_ITEM`, quatro):
 * o serviço aceita até quatro casas na borda, e um formatador com o padrão do `Intl` (três)
 * arredondaria na tela um número que o serviço guardou inteiro — a tela mentiria sobre o dado.
 *
 * `minimumFractionDigits` fica em zero: `3` continua `3`, e não vira `3,0000`.
 */
export const QUANTIDADE = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
})

/** Os rótulos das quatro colunas da linha, escritos uma vez e usados nas duas árvores. */
const ROTULOS_DO_ITEM = {
  descricao: 'Descrição',
  quantidade: 'Qtd.',
  preco: 'Preço unit.',
  total: 'Total',
} as const

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * A quantidade em pt-BR; traço quando ela não veio. Ela não é dinheiro, e não some para a SDR.
 *
 * O que não for número volta **verbatim**, e não como `NaN`: o serviço promete um decimal em texto,
 * e um texto inesperado na tela é feio — um `NaN` é pior, porque parece um dado. É a mesma decisão
 * de `dataPuraPorExtenso` diante de um formato que ela não reconhece.
 */
function quantidade(item: ItemDoOrcamento): string {
  if (item.quantidade === null || item.quantidade === undefined) {
    return VAZIO_DO_CAMPO
  }
  const numero = Number(item.quantidade)
  return Number.isFinite(numero) ? QUANTIDADE.format(numero) : item.quantidade
}

/** A descrição da linha; traço quando ela não veio. Também não é dinheiro, e também não some. */
function descricao(item: ItemDoOrcamento): string {
  return item.descricao ?? VAZIO_DO_CAMPO
}

/**
 * O rótulo do Orçamento no cabeçalho: o `numero` do ERP, e o `orcamento_erp_id` quando ele falta.
 *
 * O traço seria a resposta errada aqui, e a diferença é o que a resposta **já traz**:
 * `orcamento_erp_id` é a identidade do Orçamento no Carga Máquina — obrigatório no contrato do
 * conector, e a chave pela qual um reenvio atualiza esta mesma linha. Mostrar `—` sobre um bloco que
 * carrega aquele identificador faria a tela dizer "não sei qual Orçamento é este" ao lado do dado
 * que responde exatamente isso — e quem fosse conferir no ERP não teria por onde começar.
 *
 * `numero` continua vindo primeiro porque é ele o rótulo **humano**: é o que o vendedor lê na tela
 * do Carga Máquina e repete ao telefone.
 */
function rotuloDoOrcamento(orcamento: OrcamentoDoNegocio): string {
  return orcamento.numero ?? orcamento.orcamento_erp_id
}

/** Uma célula de dinheiro da tabela de mesa (só existe para quem vê dinheiro). */
function CelulaDeDinheiro({ valor }: { valor: string | null | undefined }) {
  return <td>{textoDoValor(valor).texto}</td>
}

/** O mesmo par na árvore de celular, onde cada campo já tem o rótulo em cima do valor. */
function DinheiroDaLinha({
  rotulo,
  valor,
}: {
  rotulo: string
  valor: string | null | undefined
}) {
  return (
    <span className="orcamento__par">
      <span className="orcamento__rotulo">{rotulo}</span>
      <span className="orcamento__campo">{textoDoValor(valor).texto}</span>
    </span>
  )
}

/**
 * Um Orçamento: o cabeçalho de três campos e as linhas.
 *
 * Ele é um componente próprio porque a lista pode ter **mais de um** — um Negócio recebe quantos
 * Orçamentos o ERP exportar para ele, e nenhum Negócio novo nasce disso (FR-14).
 */
function Orcamento({ orcamento, ehCelular }: { orcamento: OrcamentoDoNegocio; ehCelular: boolean }) {
  const valor = textoDoValor(orcamento.valor)
  const itens = orcamento.itens
  /* 11.4 (State Patterns): para a SDR a chave `valor` não vem, e o dinheiro **não existe** na tela
     — nem o valor do cabeçalho, nem as colunas de preço e total (nem traço, nem frase). */
  const semDinheiro = !('valor' in orcamento)

  return (
    <li className="orcamento__bloco">
      <div className="orcamento__cabecalho">
        <span className="orcamento__numero">{rotuloDoOrcamento(orcamento)}</span>
        {semDinheiro ? null : (
          <span className="orcamento__valor">{valor.texto}</span>
        )}
      </div>
      <p className="orcamento__validade">
        <span className="orcamento__rotulo">Validade</span>
        <span className="orcamento__campo">
          {orcamento.validade === null ? VAZIO_DO_CAMPO : dataPuraPorExtenso(orcamento.validade)}
        </span>
      </p>

      {itens.length === 0 ? (
        <p className="ficha__vazio">Este orçamento veio sem linhas.</p>
      ) : ehCelular ? (
        <ul className="orcamento__linhas">
          {itens.map((item, indice) => (
            // A chave é o índice porque a linha **não tem id**: ela é um objeto dentro da coluna
            // `itens`, e não uma entidade. A lista é imutável (o espelho só muda quando o ERP
            // reexporta, e aí o cartão inteiro é relido), então não há reordenação a confundir.
            <li className="orcamento__linha" key={indice}>
              <span className="orcamento__descricao">{descricao(item)}</span>
              <div className="orcamento__numeros">
                <span className="orcamento__par">
                  <span className="orcamento__rotulo">{ROTULOS_DO_ITEM.quantidade}</span>
                  <span className="orcamento__campo">{quantidade(item)}</span>
                </span>
                {semDinheiro ? null : (
                  <>
                    <DinheiroDaLinha rotulo={ROTULOS_DO_ITEM.preco} valor={item.preco_unitario} />
                    <DinheiroDaLinha rotulo={ROTULOS_DO_ITEM.total} valor={item.valor_total} />
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <table className="orcamento__tabela">
          <thead>
            <tr>
              <th scope="col">{ROTULOS_DO_ITEM.descricao}</th>
              <th scope="col">{ROTULOS_DO_ITEM.quantidade}</th>
              {semDinheiro ? null : (
                <>
                  <th scope="col">{ROTULOS_DO_ITEM.preco}</th>
                  <th scope="col">{ROTULOS_DO_ITEM.total}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {itens.map((item, indice) => (
              <tr key={indice}>
                <td>{descricao(item)}</td>
                <td>{quantidade(item)}</td>
                {semDinheiro ? null : (
                  <>
                    <CelulaDeDinheiro valor={item.preco_unitario} />
                    <CelulaDeDinheiro valor={item.valor_total} />
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </li>
  )
}

export function OrcamentoDoNegocio({ negocioId, codigo }: { negocioId: string; codigo?: number }) {
  const ehCelular = useEhCelular()
  const consulta = useOrcamentosDoNegocio(negocioId)
  const orcamentos = consulta.data?.itens ?? []

  return (
    <section className="ficha__card" aria-labelledby={ID_DO_TITULO}>
      <div className="ficha__card-topo">
        <span className="ficha__card-titulo" id={ID_DO_TITULO}>
          {TITULO}
        </span>
        <span className="ficha__selo">{SELO}</span>
      </div>
      <div className="ficha__card-corpo">
        {consulta.isError ? (
          /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
          <p className="ficha__erro" role="alert">
            {mensagemDe(consulta.error)}
          </p>
        ) : /* `|| consulta.data === undefined` fecha o terceiro estado: sem rede, a consulta fica
              **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela desenharia o
              estado vazio como se o Negócio não tivesse orçamento. */
        consulta.isLoading || consulta.data === undefined ? (
          <p className="ficha__ajuda" role="status">
            {CARREGANDO}
          </p>
        ) : orcamentos.length === 0 ? (
          <p className="ficha__vazio">{vazioComCodigo(codigo)}</p>
        ) : (
          <ul className="orcamento__lista">
            {orcamentos.map((orcamento) => (
              <Orcamento key={orcamento.id} orcamento={orcamento} ehCelular={ehCelular} />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
